import { useMutation, useQueryClient } from "@tanstack/react-query";
import { InviteUserFormData, UpdateUserFormData } from "../../types";
import toast from "react-hot-toast";
import { USER_ERROR_MESSAGES, USER_SUCCESS_MESSAGES } from '../../constants/index'
import userService from "../../../../services/userService";

export const useInviteUserMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ storeId, userData }: { storeId: string; userData: InviteUserFormData }) =>
            userService.inviteUserToStore(storeId, userData),
        onSuccess: (response, { storeId }) => {
        if (response.success) {
            toast.success(USER_SUCCESS_MESSAGES.USER_INVITED);
            queryClient.invalidateQueries({ queryKey: ['store-users', storeId] });
        } else {
            toast.error(response.error || USER_ERROR_MESSAGES.INVITE_USER_FAILED);
        }
    },
    onError: (error: any) => {
        console.error('Invite user error:', error?.response?.status || error?.message);
    },
});
};

export const useUpdateUserMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({
            storeId,
            userId,
            userData
        }: {
            storeId: string;
            userId: string;
            userData: UpdateUserFormData
        }) => userService.editUserInStore(storeId, userId, userData),
        onSuccess: (response, { storeId }) => {
            if (response.success) {
                toast.success(USER_SUCCESS_MESSAGES.USER_UPDATED);
                queryClient.invalidateQueries({ queryKey: ['store-users', storeId] });
            } else {
                toast.error(response.error || USER_ERROR_MESSAGES.UPDATE_USER_FAILED);
            }
        },
        onError: (error: any) => {
            console.error('Update user error:', error?.response?.status || error?.message);
            toast.error(error?.response?.data?.detail || USER_ERROR_MESSAGES.UPDATE_USER_FAILED);
        },
    });
};

export const useDeleteUserMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ storeId, userId }: { storeId: string; userId: string }) =>
            userService.deleteUserFromStore(storeId, userId),
        onSuccess: (response, { storeId }) => {
            if (response.success) {
                toast.success(USER_SUCCESS_MESSAGES.USER_DELETED);
                queryClient.invalidateQueries({ queryKey: ['store-users', storeId] });
            } else {
                toast.error(response.error || USER_ERROR_MESSAGES.DELETE_USER_FAILED);
            }
        },
        onError: (error: any) => {
            console.error('Delete user error:', error?.response?.status || error?.message);
            toast.error(error?.response?.data?.detail || USER_ERROR_MESSAGES.DELETE_USER_FAILED);
        },
    });
};

export const useSetCashierModePinMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({
            storeId,
            userId,
            pin
        }: {
            storeId: string;
            userId: string;
            pin: string | null;
        }) => userService.setCashierModePin(storeId, userId, pin),
        onSuccess: (response, { storeId, pin }) => {
            if (response.success) {
                toast.success(pin === null
                    ? USER_SUCCESS_MESSAGES.CASHIER_MODE_PIN_CLEARED
                    : USER_SUCCESS_MESSAGES.CASHIER_MODE_PIN_UPDATED);
                queryClient.invalidateQueries({ queryKey: ['store-users', storeId] });
            } else {
                toast.error(response.error || USER_ERROR_MESSAGES.UPDATE_CASHIER_MODE_PIN_FAILED);
            }
        },
        onError: (error: any) => {
            if (error?.response?.status === 403) {
                toast.error(USER_ERROR_MESSAGES.UPDATE_CASHIER_MODE_PIN_FORBIDDEN);
                return;
            }
            toast.error(error?.response?.data?.detail || USER_ERROR_MESSAGES.UPDATE_CASHIER_MODE_PIN_FAILED);
        },
    });
};
